import { DateAdapter } from "@/infrastructure/adapters/dayJs/DayJs.adapter";
import { DeductionDocument, DeductionDocumentProps } from "../deduction/DeductionDocument";
import { DateService } from "@/core/services/Date.service";
import { NumberFormatType } from "@/core/types";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";
import { NumberFormatterService } from "@/core/services";
import { DeductionStageProps, DeductionStage } from "./DeductionStage";
import { DeductionStatusProps, DeductionStatus } from "./DeductionStatus";
import { DeductionTypeCategory, DeductionTypeCategoryProps } from "./DeductionTypeCategory";
import { DeductionControl } from "./DeductionControls";
import { DeductionSplitDisplay, DeductionSplitDisplayProps } from "./DeductionSplitDisplay";

export type StageStatusActions =
  | "showDisputeButton"
  | "canEditStageStatusValidity"
  | "showApprovalButton"
  | "canSelectAutoMatch"
  | "isDeductionDrawerEditable";
export interface LatestApprovalCommentProps {
  senderId: number;
  senderFullName: string;
  senderAvatar: string;
  isSenderDeleted: boolean;
  createdAt: string;
  messageId: number;
  contentHtml: string;
  contentJson: unknown;
}

export class LatestApprovalComment implements LatestApprovalCommentProps {
  private readonly _senderId: number;
  private readonly _senderFullName: string;
  private readonly _senderAvatar: string;
  private readonly _isSenderDeleted: boolean;
  private readonly _createdAt: string;
  private readonly _messageId: number;
  private readonly _contentHtml: string;
  private readonly _contentJson: unknown;

  constructor(props: LatestApprovalCommentProps) {
    this._senderId = props.senderId;
    this._senderFullName = props.senderFullName;
    this._senderAvatar = props.senderAvatar;
    this._isSenderDeleted = props.isSenderDeleted;
    this._createdAt = props.createdAt;
    this._messageId = props.messageId;
    this._contentHtml = props.contentHtml;
    this._contentJson = props.contentJson;
  }

  public get senderId(): number {
    return this._senderId;
  }

  public get senderFullName(): string {
    return this._senderFullName;
  }

  public get senderAvatar(): string {
    return this._senderAvatar;
  }

  public get isSenderDeleted(): boolean {
    return this._isSenderDeleted;
  }

  public get createdAt(): string {
    return this._createdAt;
  }

  public createdAtFormatted(format: string): string {
    return new DateService(new DateAdapter()).format(this._createdAt, format);
  }

  public get messageId(): number {
    return this._messageId;
  }

  public get contentHtml(): string {
    return this._contentHtml;
  }

  public get contentJson(): unknown {
    return this._contentJson;
  }
}

export interface DeductionProps {
  id: number;
  checkNumber: string;
  checkDate: string;
  claimNumber: string;
  invoiceDate: string;
  age?: number | null;
  period: string;
  glCodeId: number;
  glCode: string;
  poNumber: string;
  purchaserId: number;
  purchaserKey: string;
  purchaser: string;
  description: string;
  amount: number;
  totalAmount: number;
  deductionDate: string;
  deductionTypeId: number;
  deductionTypeKey: string;
  deductionType: string;
  deductionNumber: string;
  deductionSubTypeId: number;
  deductionSubTypeKey: string;
  deductionSubType: string;
  deductionSubTypeHelpText: string | null;
  remittanceItemId: number;
  distributionCenter: string;
  distributionCenterCode: string;
  priorityId: number;
  priorityKey: string;
  priorityName: string;
  isPredictedPriority?: boolean;
  validityId: number;
  validityKey: string;
  validityName: string;
  rcaDescription: string;
  rcaIds: number[];
  analystUserId: number;
  analystUserFullName: string;
  analystUserAvatar: string;
  isAnalystDeleted: boolean;
  approverUserId: number;
  approverUserFullName: string;
  approverUserAvatar: string;
  isApproverDeleted: boolean;
  retailerName: string;
  retailerKey: string;
  reasonCode: string;
  stages: DeductionStageProps[];
  status: DeductionStatusProps;
  documents: DeductionDocumentProps[];
  isDisputed: boolean;
  canDispute: boolean;
  isManuallyUploaded: boolean;
  isIncompleteDeduction: boolean;
  latestApprovalComments?: LatestApprovalCommentProps[];
  deductionTypeCategory: DeductionTypeCategoryProps;
  controls: DeductionControl[];
  feeBucket?: string | null;
  feeBucketId?: number | null;
  feeBucketLabel?: string | null;
  splitDisplay?: DeductionSplitDisplayProps | null;
}

export class Deduction implements DeductionProps {
  private readonly _id: number;
  private readonly _checkNumber: string;
  private readonly _checkDate: string;
  private readonly _claimNumber: string;
  private readonly _invoiceDate: string;
  private readonly _age?: number | null;
  private readonly _period: string;
  private readonly _glCodeId: number;
  private readonly _glCode: string;
  private readonly _poNumber: string;
  private readonly _purchaserId: number;
  private readonly _purchaserKey: string;
  private readonly _purchaser: string;
  private readonly _description: string;
  private readonly _amount: number;
  private readonly _totalAmount: number;
  private readonly _deductionDate: string;
  private readonly _deductionTypeId: number;
  private readonly _deductionTypeKey: string;
  private readonly _deductionType: string;
  private readonly _deductionNumber: string;
  private readonly _deductionSubTypeId: number;
  private readonly _deductionSubTypeKey: string;
  private readonly _deductionSubType: string;
  private readonly _deductionSubTypeHelpText: string | null;
  private readonly _remittanceItemId: number;
  private readonly _distributionCenter: string;
  private readonly _distributionCenterCode: string;
  private readonly _priorityId: number;
  private readonly _priorityKey: string;
  private readonly _priorityName: string;
  private readonly _isPredictedPriority?: boolean;
  private readonly _validityId: number;
  private readonly _validityKey: string;
  private readonly _validityName: string;
  private readonly _rcaDescription: string;
  private readonly _rcaIds: number[];
  private readonly _analystUserId: number;
  private readonly _analystUserFullName: string;
  private readonly _analystUserAvatar: string;
  private readonly _isAnalystDeleted: boolean;
  private readonly _approverUserId: number;
  private readonly _approverUserFullName: string;
  private readonly _approverUserAvatar: string;
  private readonly _isApproverDeleted: boolean;
  private readonly _retailerName: string;
  private readonly _retailerKey: string;
  private readonly _reasonCode: string;
  private readonly _stages: DeductionStageProps[];
  private readonly _status: DeductionStatusProps;
  private _documents: DeductionDocumentProps[];
  private readonly _isDisputed: boolean;
  private readonly _canDispute: boolean;
  private readonly _isManuallyUploaded: boolean;
  private readonly _isIncompleteDeduction: boolean;
  private readonly _latestApprovalComments?: LatestApprovalCommentProps[];
  private readonly _currentStageKey?: string;
  private readonly _currentStatusKey?: string;
  private readonly _deductionTypeCategory: DeductionTypeCategoryProps;
  private readonly _controls: DeductionControl[];
  private readonly _feeBucket: string | null;
  private readonly _feeBucketId: number | null;
  private readonly _feeBucketLabel: string | null;
  private readonly _splitDisplay: DeductionSplitDisplayProps | null;

  constructor(props: DeductionProps) {
    this._id = props.id;
    this._checkNumber = props.checkNumber;
    this._checkDate = props.checkDate;
    this._claimNumber = props.claimNumber;
    this._invoiceDate = props.invoiceDate;
    this._age = props.age;
    this._period = props.period;
    this._glCodeId = props.glCodeId;
    this._glCode = props.glCode;
    this._poNumber = props.poNumber;
    this._purchaserId = props.purchaserId;
    this._purchaserKey = props.purchaserKey;
    this._purchaser = props.purchaser;
    this._description = props.description;
    this._amount = props.amount;
    this._totalAmount = props.totalAmount;
    this._deductionDate = props.deductionDate;
    this._deductionTypeId = props.deductionTypeId;
    this._deductionTypeKey = props.deductionTypeKey;
    this._deductionType = props.deductionType;
    this._deductionNumber = props.deductionNumber;
    this._deductionSubTypeId = props.deductionSubTypeId;
    this._deductionSubTypeKey = props.deductionSubTypeKey;
    this._deductionSubType = props.deductionSubType;
    this._deductionSubTypeHelpText = props.deductionSubTypeHelpText;
    this._remittanceItemId = props.remittanceItemId;
    this._distributionCenter = props.distributionCenter;
    this._distributionCenterCode = props.distributionCenterCode;
    this._priorityId = props.priorityId;
    this._priorityKey = props.priorityKey;
    this._priorityName = props.priorityName;
    this._isPredictedPriority = props.isPredictedPriority;
    this._validityId = props.validityId;
    this._validityKey = props.validityKey;
    this._validityName = props.validityName;
    this._rcaDescription = props.rcaDescription;
    this._rcaIds = props.rcaIds;
    this._analystUserId = props.analystUserId;
    this._analystUserFullName = props.analystUserFullName;
    this._analystUserAvatar = props.analystUserAvatar;
    this._isAnalystDeleted = props.isAnalystDeleted;
    this._approverUserId = props.approverUserId;
    this._approverUserFullName = props.approverUserFullName;
    this._approverUserAvatar = props.approverUserAvatar;
    this._isApproverDeleted = props.isApproverDeleted;
    this._retailerName = props.retailerName;
    this._retailerKey = props.retailerKey;
    this._reasonCode = props.reasonCode;
    this._stages = props.stages;
    this._status = props.status;
    this._documents = props.documents;
    this._isDisputed = props.isDisputed;
    this._canDispute = props.canDispute;
    this._isManuallyUploaded = props.isManuallyUploaded;
    this._isIncompleteDeduction = props.isIncompleteDeduction;
    this._latestApprovalComments = props.latestApprovalComments;
    this._currentStageKey = props.stages.find(s => s.isCurrent)?.stageKey;
    this._currentStatusKey = props.status.statusKey;
    this._deductionTypeCategory = props.deductionTypeCategory;
    this._controls = props.controls;
    this._feeBucket = props.feeBucket ?? null;
    this._feeBucketId = props.feeBucketId ?? null;
    this._feeBucketLabel = props.feeBucketLabel ?? null;
    this._splitDisplay = props.splitDisplay ?? null;
  }

  public get id(): number {
    return this._id;
  }

  public get checkNumber(): string {
    return this._checkNumber;
  }

  public get checkDate(): string {
    return this._checkDate;
  }

  public get invoiceDate(): string {
    return this._invoiceDate;
  }

  public get age(): number | null | undefined {
    return this._age;
  }

  public get isClosed(): boolean {
    return this._currentStageKey === "CLOSED";
  }

  public get claimNumber(): string {
    return this._claimNumber;
  }

  public get period(): string {
    return this._period;
  }

  public get glCodeId(): number {
    return this._glCodeId;
  }

  public get glCode(): string {
    return this._glCode;
  }

  public get poNumber(): string {
    return this._poNumber;
  }

  public get purchaser(): string {
    return this._purchaser;
  }

  public get purchaserId(): number {
    return this._purchaserId;
  }

  public get purchaserKey(): string {
    return this._purchaserKey;
  }

  public get description(): string {
    return this._description;
  }

  public get amount(): number {
    return this._amount;
  }

  public amountFormatted(format: NumberFormatType): string {
    return this._amount ? new NumberFormatterService(new NumberFormatterAdapter()).format(this._amount, format) : "";
  }

  public get totalAmount(): number {
    return this._totalAmount;
  }

  public totalAmountFormatted(format: NumberFormatType): string {
    return this._totalAmount
      ? new NumberFormatterService(new NumberFormatterAdapter()).format(this._totalAmount, format)
      : "";
  }

  public get deductionDate(): string {
    return this._deductionDate;
  }

  public deductionDateFormatted(format: string): string {
    return new DateService(new DateAdapter()).format(this._deductionDate, format);
  }

  public get deductionType(): string {
    return this._deductionType;
  }

  public get deductionTypeId(): number {
    return this._deductionTypeId;
  }

  public get deductionTypeKey(): string {
    return this._deductionTypeKey;
  }

  public get deductionNumber(): string {
    return this._deductionNumber;
  }

  public get deductionSubType(): string {
    return this._deductionSubType;
  }

  public get deductionSubTypeId(): number {
    return this._deductionSubTypeId;
  }

  public get deductionSubTypeKey(): string {
    return this._deductionSubTypeKey;
  }

  public get deductionSubTypeHelpText(): string | null {
    return this._deductionSubTypeHelpText;
  }

  public get remittanceItemId(): number {
    return this._remittanceItemId;
  }

  public get distributionCenter(): string {
    return this._distributionCenter;
  }

  public get distributionCenterCode(): string {
    return this._distributionCenterCode;
  }

  public get priorityId(): number {
    return this._priorityId;
  }

  public get priorityKey(): string {
    return this._priorityKey;
  }

  public get priorityName(): string {
    return this._priorityName;
  }

  public get isPredictedPriority(): boolean | undefined {
    return this._isPredictedPriority;
  }

  public get validityId(): number {
    return this._validityId;
  }

  public get validityKey(): string {
    return this._validityKey;
  }

  public get validityName(): string {
    return this._validityName;
  }

  public get rcaDescription(): string {
    return this._rcaDescription;
  }

  public get rcaIds(): number[] {
    return this._rcaIds;
  }

  public get analystUserId(): number {
    return this._analystUserId;
  }

  public get analystUserFullName(): string {
    return this._analystUserFullName;
  }

  public get analystUserAvatar(): string {
    return this._analystUserAvatar;
  }

  public get isAnalystDeleted(): boolean {
    return this._isAnalystDeleted;
  }

  public get approverUserId(): number {
    return this._approverUserId;
  }

  public get approverUserFullName(): string {
    return this._approverUserFullName;
  }

  public get approverUserAvatar(): string {
    return this._approverUserAvatar;
  }

  public get isApproverDeleted(): boolean {
    return this._isApproverDeleted;
  }

  public get retailerName(): string {
    return this._retailerName;
  }

  public get retailerKey(): string {
    return this._retailerKey;
  }

  public get reasonCode(): string {
    return this._reasonCode;
  }

  public get stages(): DeductionStage[] {
    return this._stages.map(stage => new DeductionStage(stage));
  }

  public get status(): DeductionStatus {
    return new DeductionStatus(this._status);
  }

  public get documents(): DeductionDocument[] {
    return this._documents.map(document => new DeductionDocument(document));
  }

  public set documents(documents: DeductionDocument[]) {
    this._documents = documents;
  }

  public get isDisputed(): boolean {
    return this._isDisputed;
  }

  public get canDispute(): boolean {
    return this._canDispute;
  }

  public get isManuallyUploaded(): boolean {
    return this._isManuallyUploaded;
  }

  public get isIncompleteDeduction(): boolean {
    return this._isIncompleteDeduction;
  }

  public get latestApprovalComments(): LatestApprovalCommentProps[] {
    return this._latestApprovalComments?.map(comment => new LatestApprovalComment(comment)) ?? [];
  }

  public get deductionTypeCategory(): DeductionTypeCategory {
    return new DeductionTypeCategory(this._deductionTypeCategory);
  }

  public get controls(): DeductionControl[] {
    return this._controls.map(control => new DeductionControl(control));
  }

  public get feeBucket(): string | null {
    return this._feeBucket;
  }

  public get feeBucketId(): number | null {
    return this._feeBucketId;
  }

  public get feeBucketLabel(): string | null {
    return this._feeBucketLabel;
  }

  public get splitDisplay(): DeductionSplitDisplay | null {
    return this._splitDisplay ? new DeductionSplitDisplay(this._splitDisplay) : null;
  }

  public get splitHeaderText(): string {
    const headerText = this._splitDisplay?.headerText?.trim();
    if (headerText) {
      return headerText;
    }

    return [
      this._deductionSubType,
      this._retailerName,
      `${this._distributionCenter ?? ""} (${this._distributionCenterCode ?? ""})`.trim().replaceAll("()", ""),
    ]
      .filter(Boolean)
      .join(" · ");
  }

  private _isInApproval(): boolean {
    return this._currentStatusKey === "PENDING_APPROVAL" || this._currentStatusKey === "APPROVED";
  }

  private _updateStageControls(purpose: StageStatusActions): boolean {
    switch (purpose) {
      case "showApprovalButton":
      case "canEditStageStatusValidity":
        return (
          Boolean(this._id) &&
          this._currentStageKey === "ANALYSIS" &&
          (this._currentStatusKey === "OPEN" || this._currentStatusKey === "RETURNED") &&
          !this._isInApproval()
        );
      case "showDisputeButton":
        return (
          Boolean(this._id) &&
          !this._isDisputed &&
          this._validityKey === "INVALID" &&
          this._currentStageKey === "RESOLUTION" &&
          (this._currentStatusKey === "DISPUTE_NEEDED" || this._currentStatusKey === "DISPUTE_REJECTED") &&
          this._canDispute
        );
      case "canSelectAutoMatch":
        return (
          Boolean(this._id) &&
          (this._currentStageKey === "ANALYSIS" || this._currentStatusKey === "RESOLUTION") &&
          !this._isInApproval()
        );
      case "isDeductionDrawerEditable":
        return !(Boolean(this._id) && this._validityKey === "VALID" && this._currentStatusKey === "APPROVED");
      default:
        return false;
    }
  }

  public showApprovalButton(): boolean {
    return this._updateStageControls("showApprovalButton");
  }

  public canEditStageStatusValidity(): boolean {
    return this._updateStageControls("canEditStageStatusValidity");
  }

  public showDisputeButton(): boolean {
    return this._updateStageControls("showDisputeButton");
  }

  public isDeductionDrawerEditable(): boolean {
    return this._updateStageControls("isDeductionDrawerEditable");
  }

  public canSelectAutoMatch(): boolean {
    return this._updateStageControls("canSelectAutoMatch");
  }
}
